import classNames from 'classnames'
import _ from 'lodash'
import React from 'react'
import ReactDOM from 'react-dom'
import { v4 as uuidv4 } from 'uuid'

import { IBaseProps } from 'browser/components/atomic-elements/atoms/base-props'
import { UI2PlatformContext } from '../../forms/ui2-form/ui2-platform-context'
import { UI2FileViewer, UI2FileViewerLightbox, downloadFileEntry, printFileEntry } from 'browser/components/ui2/fields/field-ui/file-viewer/file-viewer'
import { FramesManager } from 'shared-libs/components/view/frames-manager'
import {
  convertToWebp,
  getFileMimeType,
  getFileType,
  resizeImageFile,
} from 'browser/app/utils/image'

import {
  FILE_TYPE_MIME_MAP,
  FileSetValue,
  UI2FileUploadFieldProps,
  UI2_FILE_UPLOAD_FIELD_DEFAULT_PROPS,
} from 'shared-libs/components/ui2/fields/ui2-file-upload-field/ui2-file-upload-field-props'
import {
  FileCountBounds,
  getFileEntryName,
  getFileEntryUri,
  isFileCountRequired,
  isImageFileEntry,
  getFilesetFiles,
  resolveFileCountBounds,
  withFilesetFiles,
} from 'shared-libs/components/ui2/fields/core-field/media-attachment-field-props'
import { UI2FieldShell, IUI2FieldShellProps } from '../field-ui/field-shell/field-shell'
import { UI2FieldControl } from '../field-ui/field-control/field-control'
import { UI2EmptyStateLauncher } from '../field-ui/empty-state-launcher/empty-state-launcher'
import { UI2PulseHighlight, IUI2PulseHighlightRef } from '../../behavior/pulse-highlight/pulse-highlight'

import { getFileTypeIcon } from 'shared-libs/components/ui2/icons/file-type-icons'
import { UI2Icon } from 'browser/components/ui2/display/ui2-icon/ui2-icon'
import { UI2IconNames } from 'shared-libs/components/ui2/icons/ui2-icon-names'
import './_ui2-file-upload-field.scss'

const DEFAULT_EMPTY_BUTTON_TEXT = 'browse files'
const DEFAULT_EMPTY_MESSAGE_MW = 'Tap to upload files'
const DEFAULT_EMPTY_READ_ONLY_TEXT = 'No files available'
const DEFAULT_ADD_MORE_TEXT = 'Add files'
const DEFAULT_MAX_SHORT_EDGE = 2350
const DEFAULT_WEBP_QUALITY = 0.75

interface IUI2FileUploadFieldWebExtras extends Omit<IBaseProps, 'children'> {
  onChange?: (value: any) => void
  errors?: any[]
  frames?: FramesManager
  translationTable?: any
}

export interface IUI2FileUploadFieldProps
  extends UI2FileUploadFieldProps,
    IUI2FileUploadFieldWebExtras {}

export interface IUI2FileUploadFieldState {
  isDragging: boolean
  lightboxIndex: number | null
  recentlyAddedIds: Set<string>
  /** True from the moment files are handed over until their entries land in the value. */
  isProcessing: boolean
}

/** Returns the URI of the smallest available thumbnail for a file entry, or null. */
function getChipThumbnailUri(file: any): string | null {
  const pickSmallest = (sources: any[]): string | null => {
    if (!sources?.length) return null
    return [...sources].sort((a, b) => (a.width || 0) - (b.width || 0))[0]?.uri ?? null
  }
  if (file?.type === 'image') return pickSmallest(file.source) || file.uri || null
  if (file?.type === 'pdf' && file.preview?.length > 0) {
    const page = file.preview[0]
    return pickSmallest(page.source) || page.uri || null
  }
  return null
}

/** Map a file entry to the appropriate Blueprint icon name based on its type/extension. */
export class UI2FileUploadField extends React.Component<IUI2FileUploadFieldProps, IUI2FileUploadFieldState> {
  static contextType = UI2PlatformContext
  declare context: React.ContextType<typeof UI2PlatformContext>

  public static defaultProps: Partial<IUI2FileUploadFieldProps> = {
    ...UI2_FILE_UPLOAD_FIELD_DEFAULT_PROPS,
    isHorizontalLayout: false,
    addMoreMessage: DEFAULT_ADD_MORE_TEXT,
    maxImageDimension: DEFAULT_MAX_SHORT_EDGE,
    imageQuality: DEFAULT_WEBP_QUALITY,
  }

  public state: IUI2FileUploadFieldState = {
    isDragging: false,
    lightboxIndex: null,
    recentlyAddedIds: new Set(),
    isProcessing: false,
  }

  protected inputRef = React.createRef<HTMLInputElement>()
  protected pulseRef = React.createRef<IUI2PulseHighlightRef>()
  private dragDepth = 0

  constructor(props: IUI2FileUploadFieldProps) {
    super(props)
  }

  public render(): React.ReactNode {
    const { className, style } = this.props
    const files = this.getValue()
    const isEmpty = _.isEmpty(files)
    const isMobileWeb = this.context === 'mobileWeb'
    // On MW the empty state is a full-bleed launcher button that owns its own
    // border. Wrapping it in UI2FieldControl would add unwanted padding and
    // trigger the :focus-within blue-border rule. Render it directly instead.
    const isMobileEmpty = this.usesMobileEmptyLauncher(isEmpty)
    // Don't enter prefilled display when the file count is below the minimum —
    // the user still needs to act, so the field should stay in edit mode.
    const { min } = this.getEffectiveBounds()
    const effectiveEnablePrefilledMode = this.props.enablePrefilledMode && (min === 0 || files.length >= min)
    return (
      <>
        <UI2FieldShell
          {...(this.props as IUI2FieldShellProps<any[]>)}
          enablePrefilledMode={effectiveEnablePrefilledMode}
          // A positive `minFiles` makes the field required in every sense the
          // shell cares about — asterisk, submit-time gate — so the count
          // error below only has to cover the partially-filled case.
          isRequired={this.isCountRequired()}
          errors={this.props.errors}
          isLoading={this.state.isProcessing}
          isValueEmpty={UI2FileUploadField.isValueEmpty}
          emptyText={this.props.emptyText ?? this.defaultEmptyReadOnlyText()}
          className={classNames('ui2-fileUploadField', className, {
            'ui2-fileUploadField--isEmpty': isEmpty,
          })}
          style={style}
          prefilledEditText={this.props.prefilledEditText ?? this.defaultPrefilledEditText()}
          bodyIsFieldControl={!isMobileEmpty && !isMobileWeb}
          pulseRef={this.getShellPulseRef(isMobileEmpty)}
          renderEditable={isMobileWeb && !isEmpty ? this.renderMWEditable : undefined}
          renderReadOnly={this.renderReadOnly}
        >
          {isMobileEmpty
            ? this.renderEmptyStateMobile()
            : isMobileWeb
              ? this.renderBody()
              : <UI2FieldControl>{this.renderBody()}</UI2FieldControl>
          }
        </UI2FieldShell>
        {this.renderLightbox()}
      </>
    )
  }

  // Called by the renderer's resolveFieldValidation (blocks Next) and
  // mergeUIStateErrors (injects error text on submit). Same semantics as the NM twin.
  public static getCountErrors(value: FileSetValue | undefined, props: any): string[] | undefined {
    const { isReadOnly, isDisabled, isOptional } = props
    if (isReadOnly || isDisabled || isOptional) return undefined
    if (!isFileCountRequired(props)) return undefined
    const bounds = resolveFileCountBounds(props)
    const count = getFilesetFiles(value as any).length
    if (count < bounds.min) {
      return [`at least ${bounds.min} ${bounds.min === 1 ? 'file' : 'files'} required`]
    }
    if (bounds.max != null && count > bounds.max) {
      return [`no more than ${bounds.max} ${bounds.max === 1 ? 'file' : 'files'} allowed`]
    }
    return undefined
  }

  private static isValueEmpty = (value: FileSetValue | undefined): boolean => {
    return _.isEmpty(getFilesetFiles(value as any))
  }

  private renderLightbox(): React.ReactNode {
    const { lightboxIndex } = this.state
    if (lightboxIndex === null || typeof document === 'undefined') return null
    // The lightbox renders an <img>, so it only ever holds the image entries —
    // indices below are into this list, not into the full value.
    const files = this.getImageFiles()
    if (_.isEmpty(files)) return null
    const total = files.length
    const index = Math.min(lightboxIndex, total - 1)
    const current = files[index]
    return ReactDOM.createPortal(
      <UI2FileViewerLightbox
        images={files}
        index={index}
        current={current}
        total={total}
        caption={this.getLightboxCaption(current)}
        onPrev={this.handleLightboxPrev}
        onNext={this.handleLightboxNext}
        onClose={this.handleLightboxClose}
        actions={this.renderLightboxActions(current)}
      />,
      document.body,
    )
  }

  private renderLightboxActions(current: any): React.ReactNode {
    const { showDownloadButton, showPrintButton, entity, printOptions } = this.props
    if (!showDownloadButton && !showPrintButton) return undefined
    return (
      <>
        {showDownloadButton && (
          <button
            className='ui2-fileViewer-actionBarButton'
            type='button'
            onClick={(e) => { e.stopPropagation(); downloadFileEntry(current) }}
            aria-label='Download'
            title='Download'
          >
            <UI2Icon name={UI2IconNames.DOWNLOAD} size={18} />
          </button>
        )}
        {showPrintButton && (
          <button
            className='ui2-fileViewer-actionBarButton'
            type='button'
            onClick={(e) => { e.stopPropagation(); printFileEntry(current, entity, printOptions) }}
            aria-label='Print'
            title='Print'
          >
            <UI2Icon name={UI2IconNames.PRINTER} size={18} />
          </button>
        )}
      </>
    )
  }

  /**
   * Which ring the shell registers and pulses. Only the MW empty state has one
   * by default — the launcher button, rather than the empty space around it.
   * Photo capture redirects this at its tile row.
   */
  protected getShellPulseRef(isMobileEmpty: boolean): React.RefObject<any> | undefined {
    return isMobileEmpty ? this.pulseRef : undefined
  }

  /** Title for the lightbox. Only photo capture has names for its files. */
  protected getLightboxCaption(_current: any): string | undefined {
    return undefined
  }

  /** Positional entry point kept for subclasses that render by index. */
  protected handleChipClick = (index: number) => {
    const file = this.getValue()[index]
    if (file) this.handleOpenFile(file)
  }

  /**
   * The one gate into previewing a file. Images and PDFs with server-rendered
   * preview pages open the lightbox; everything else opens in a new tab.
   * A disabled field previews nothing.
   */
  protected handleOpenFile = (file: any) => {
    if (this.props.isDisabled) return
    if (isImageFileEntry(file)) {
      const imageIndex = this.getImageFiles().indexOf(file)
      this.setState({ lightboxIndex: Math.max(0, imageIndex) })
      return
    }
    if (file?.type === 'pdf' && file.preview?.length > 0) {
      const allImages = this.getImageFiles()
      const firstPreviewId = file.preview[0]?.uniqueId
      const idx = firstPreviewId
        ? allImages.findIndex((img: any) => img.uniqueId === firstPreviewId)
        : 0
      this.setState({ lightboxIndex: Math.max(0, idx) })
      return
    }
    const uri = getFileEntryUri(file)
    if (uri) window.open(uri, '_blank', 'noopener,noreferrer')
  }

  private handleLightboxClose = () => {
    this.setState({ lightboxIndex: null })
  }

  private handleLightboxPrev = () => {
    this.setState(({ lightboxIndex }) => ({
      lightboxIndex: lightboxIndex === null ? null : Math.max(0, lightboxIndex - 1),
    }))
  }

  private handleLightboxNext = () => {
    const total = this.getImageFiles().length
    this.setState(({ lightboxIndex }) => ({
      lightboxIndex: lightboxIndex === null ? null : Math.min(total - 1, lightboxIndex + 1),
    }))
  }

  protected renderBody(): React.ReactNode {
    const files = this.getValue()
    return _.isEmpty(files) ? this.renderEmptyState() : this.renderChipList(files)
  }

  /**
   * Whether an empty body renders as the bare MW launcher button. Subclasses
   * whose empty body is a real value area — photo capture's dashed required
   * slots — say no, so the body goes through `renderBody` like any other.
   */
  protected usesMobileEmptyLauncher(isEmpty: boolean): boolean {
    return isEmpty && this.context === 'mobileWeb'
  }

  // renderReadOnly places the Edit action *below* the viewer, right-aligned.
  // This keeps the carousel the same size whether in prefilled or read-only state
  // and avoids consuming horizontal space in the label/value grid.
  private renderReadOnly = (_value: any, { editNode }: { editNode?: React.ReactNode }): React.ReactNode => {
    const { showFileName } = this.props
    const files = this.getValue()
    return (
      <div className='ui2-fileUploadField-readOnly'>
        {!_.isEmpty(files) && (
          <UI2FileViewer
            {..._.omit(this.props as any, ['label', 'requiredType', 'tooltip', 'inlineHelpText', 'errors'])}
            showFileName={showFileName !== false}
            value={{ files }}
          />
        )}
        {editNode && <div className='ui2-fileUploadField-editRow'>{editNode}</div>}
      </div>
    )
  }

  // MW only: places the Cancel button below the chip body, right-aligned.
  // DW uses the FieldControl cancelSlot (bodyIsFieldControl=true) instead.
  private renderMWEditable = (children: React.ReactNode, { cancelNode }: { cancelNode?: React.ReactNode }): React.ReactNode => {
    if (!cancelNode) return children
    return (
      <>
        {children}
        <div className='ui2-fileUploadField-cancelRow'>{cancelNode}</div>
      </>
    )
  }

  /**
   * Wording for the read-only empty state. Overridden by subclasses that
   * hold something other than files (photo capture holds images), and
   * overridden per-form by `emptyText` from uiSchema.
   */
  protected defaultEmptyReadOnlyText(): string {
    return DEFAULT_EMPTY_READ_ONLY_TEXT
  }

  /** Subclass hook for a default prefilled Edit label (e.g. "Add/Remove Photos"). */
  protected defaultPrefilledEditText(): string | undefined {
    return undefined
  }

  // Wording hooks for the DW drop zone and the add affordance. Subclasses
  // override the strings rather than the markup, so they keep the drop zone,
  // the drag-hover treatment and the chip row for free.
  protected defaultDropZoneText(): string {
    return 'Drop files here, or'
  }

  protected defaultBrowseButtonText(): string {
    return DEFAULT_EMPTY_BUTTON_TEXT
  }

  protected defaultAddMoreText(): string {
    return DEFAULT_ADD_MORE_TEXT
  }

  protected defaultEmptyMessageMobile(): string {
    return DEFAULT_EMPTY_MESSAGE_MW
  }

  protected renderEmptyState(): React.ReactNode {
    const isMobileWeb = this.context === 'mobileWeb'
    return isMobileWeb ? this.renderEmptyStateMobile() : this.renderEmptyStateDesktop()
  }

  protected renderEmptyStateDesktop(): React.ReactNode {
    const { placeholder, isDisabled } = this.props
    const { isDragging } = this.state
    const buttonText = placeholder || this.defaultBrowseButtonText()
    return (
      <>
        <div
          className={classNames('ui2-fileUploadField-dropZone', {
            'ui2-fileUploadField-dropZone--isDragging': isDragging,
            'ui2-fileUploadField-dropZone--isDisabled': isDisabled,
          })}
          onDragEnter={isDisabled ? undefined : this.handleDragEnter}
          onDragOver={isDisabled ? undefined : this.handleDragOver}
          onDragLeave={isDisabled ? undefined : this.handleDragLeave}
          onDrop={isDisabled ? undefined : this.handleDrop}
        >
          <>
            <span className='ui2-fileUploadField-dropZoneText'>{this.defaultDropZoneText()}</span>
            <button
              type='button'
              className='ui2-fileUploadField-dropZoneButton'
              onClick={this.handleOpenPicker}
              disabled={isDisabled}
            >
              {buttonText}
            </button>
          </>
        </div>
        {this.renderHiddenInput()}
      </>
    )
  }

  protected renderEmptyStateMobile(): React.ReactNode {
    const { placeholder, isDisabled } = this.props
    const message = placeholder || this.defaultEmptyMessageMobile()
    return (
      <>
        <UI2PulseHighlight ref={this.pulseRef}>
          <UI2EmptyStateLauncher
            onPress={this.handleOpenPicker}
            isDisabled={!!isDisabled}
            message={message}
            icon={<UI2Icon name={UI2IconNames.UPLOAD} size={16} />}
          />
        </UI2PulseHighlight>
        {this.renderHiddenInput()}
      </>
    )
  }

  protected renderChipList(files: any[]): React.ReactNode {
    const canRemove = !this.props.isDisabled && !this.props.isReadOnly
    return this.renderPopulatedBody(
      <div className='ui2-fileUploadField-chipRow'>
        {files.map((file: any, index: number) => this.renderFileChip(file, index, canRemove))}
        {this.canAddMore(files.length) && this.renderAddChip()}
      </div>,
      files.length,
    )
  }

  protected canAddMore(count: number): boolean {
    const { isDisabled, isReadOnly } = this.props
    return !isDisabled && !isReadOnly && !this.isAtMax(count)
  }

  /** The inline "Add files" affordance that sits at the end of the chip row. */
  protected renderAddChip(): React.ReactNode {
    const { addMoreMessage } = this.props
    return (
      <button
        type='button'
        className='ui2-fileUploadField-chip ui2-fileUploadField-chip--add'
        onClick={this.handleOpenPicker}
      >
        <UI2Icon name={UI2IconNames.PLUS} size={14} />
        <span className='ui2-fileUploadField-chipAddLabel'>
          {addMoreMessage || this.defaultAddMoreText()}
        </span>
      </button>
    )
  }

  /**
   * Wraps a populated editable body (chips, thumbnails, dashed slots) in the
   * hidden input and — on DW — the drop target, so files can be added by
   * dragging onto what is already there. MW gets neither container: touch
   * devices don't drag-drop, and the extra div only added padding.
   */
  protected renderPopulatedBody(body: React.ReactNode, fileCount: number): React.ReactNode {
    if (this.context === 'mobileWeb') {
      return (
        <>
          {body}
          {this.renderHiddenInput()}
        </>
      )
    }
    const canAddMore = this.canAddMore(fileCount)
    const { isDragging } = this.state
    return (
      <div
        className={classNames('ui2-fileUploadField-chipContainer', {
          'ui2-fileUploadField-chipContainer--isDragging': isDragging && canAddMore,
        })}
        onDragEnter={canAddMore ? this.handleDragEnter : undefined}
        onDragOver={canAddMore ? this.handleDragOver : undefined}
        onDragLeave={canAddMore ? this.handleDragLeave : undefined}
        onDrop={canAddMore ? this.handleDrop : undefined}
      >
        {body}
        {this.renderHiddenInput()}
      </div>
    )
  }

  // Shared by the editable body and the read-only document list. A disabled
  // field's chips are inert — no click target, no keyboard focus, no remove.
  protected renderFileChip(file: any, index: number, canRemove: boolean): React.ReactNode {
    const { isDisabled } = this.props
    const name = getFileEntryName(file)
    const canOpen = !isDisabled
    const isNew = !!file?.uniqueId && this.state.recentlyAddedIds.has(file.uniqueId)
    const thumbnailUri = getChipThumbnailUri(file)
    return (
      <span
        key={file?.uniqueId || `${name}-${index}`}
        role={canOpen ? 'button' : undefined}
        tabIndex={canOpen ? 0 : undefined}
        className={classNames('ui2-fileUploadField-chip', {
          'ui2-fileUploadField-chip--clickable': canOpen,
          'ui2-fileUploadField-chip--disabled': isDisabled,
          'ui2-fileUploadField-chip--new': isNew,
        })}
        title={name}
        onClick={canOpen ? () => this.handleOpenFile(file) : undefined}
        onKeyDown={canOpen ? (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            this.handleOpenFile(file)
          }
        } : undefined}
      >
        {thumbnailUri ? (
          <img
            className='ui2-fileUploadField-chipThumbnail'
            src={thumbnailUri}
            alt=''
            aria-hidden='true'
          />
        ) : (
          <UI2Icon
            className='ui2-fileUploadField-chipIcon'
            name={getFileTypeIcon(file)}
            size={14}
          />
        )}
        <span className='ui2-fileUploadField-chipLabel'>{name}</span>
        {canRemove && (
          <button
            type='button'
            className='ui2-fileUploadField-chipRemove'
            onClick={(e) => { e.stopPropagation(); this.handleRemoveAtIndex(index) }}
            aria-label={`Remove ${name}`}
            title='Remove file'
          >
            <UI2Icon name={UI2IconNames.X} size={12} />
          </button>
        )}
      </span>
    )
  }

  protected renderHiddenInput(): React.ReactNode {
    const { accept } = this.props
    const { max } = this.getEffectiveBounds()
    const allowMultiple = max == null || max > 1
    const acceptArr = typeof accept === 'string' ? [accept] : accept
    const acceptAttr = acceptArr?.map((t) => FILE_TYPE_MIME_MAP[t as keyof typeof FILE_TYPE_MIME_MAP] ?? t).join(',')
    return (
      <input
        ref={this.inputRef}
        type='file'
        accept={acceptAttr}
        multiple={allowMultiple}
        className='ui2-fileUploadField-hiddenInput'
        onChange={this.handleFilesSelected}
      />
    )
  }

  protected handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    this.dragDepth += 1
    if (!this.state.isDragging) this.setState({ isDragging: true })
  }

  protected handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy'
  }

  protected handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    this.dragDepth = Math.max(0, this.dragDepth - 1)
    if (this.dragDepth === 0) this.setState({ isDragging: false })
  }

  protected handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    this.dragDepth = 0
    this.setState({ isDragging: false })
    const dropped = e.dataTransfer?.files
    if (!dropped || dropped.length === 0) return
    this.ingestFiles(dropped)
  }

  protected getValue(): any[] {
    return getFilesetFiles(this.props.value as any)
  }

  protected getImageFiles(): any[] {
    const result: any[] = []
    for (const file of this.getValue()) {
      if (isImageFileEntry(file)) {
        result.push(file)
      } else if (file?.type === 'pdf' && file.preview?.length > 0) {
        result.push(...file.preview.map((page: any) => ({
          ...page,
          _pdfSourceUri: file.uri,
          _pdfSourceName: file.name,
        })))
      }
    }
    return result
  }

  // Both overridden by photo capture, whose `guidedCaptureOptions` can set the
  // minimum on its own. Keep them as the single read path — a caller that
  // reaches for `resolveFileCountBounds(this.props)` directly bypasses that.
  protected getEffectiveBounds(): FileCountBounds {
    return resolveFileCountBounds(this.props)
  }

  protected isCountRequired(): boolean {
    return isFileCountRequired(this.props)
  }

  protected isAtMax(count: number): boolean {
    const { max } = this.getEffectiveBounds()
    return max != null && count >= max
  }

  protected handleOpenPicker = () => {
    this.inputRef.current?.click()
  }

  private handleFilesSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = event.target.files
    const input = event.target
    if (fileList) this.ingestFiles(fileList)
    input.value = ''
  }

  // Mirrors the legacy `ui:fileInput` pipeline: mime detect → for images,
  // resize + WebP recompress → store as blob URL. Non-images pass through.
  protected async ingestFiles(fileList: FileList) {
    if (fileList.length === 0) return
    const { max } = this.getEffectiveBounds()
    const existing = this.getValue()
    const remaining = max != null ? Math.max(0, max - existing.length) : undefined
    const toIngest: File[] = []
    for (let i = 0; i < fileList.length; i++) {
      if (remaining != null && toIngest.length >= remaining) break
      const file = fileList.item(i)
      if (file) toIngest.push(file)
    }
    if (toIngest.length === 0) return

    const { maxImageDimension, imageQuality } = this.props
    const maxEdge = maxImageDimension ?? DEFAULT_MAX_SHORT_EDGE
    const quality = imageQuality ?? DEFAULT_WEBP_QUALITY

    await this.withProcessing(() => this.processAndCommit(toIngest, existing, maxEdge, quality))
  }

  /**
   * Scrims the field body (via the shell's `isLoading`) while `work` runs.
   * Resize + WebP recompress is seconds-long for a phone-camera image, and
   * until it lands nothing in the field acknowledges the pick. Subclasses
   * with their own async capture path — the MW document camera — route
   * through this rather than touching `isProcessing` directly.
   */
  protected async withProcessing<T>(work: () => Promise<T>): Promise<T> {
    this.setState({ isProcessing: true })
    try {
      return await work()
    } finally {
      this.setState({ isProcessing: false })
    }
  }

  private async processAndCommit(
    toIngest: File[],
    existing: any[],
    maxEdge: number,
    quality: number,
  ): Promise<void> {
    const processed = await Promise.all(toIngest.map(async (originalFile) => {
      try {
        const mimeType = await getFileMimeType(originalFile)
        const isImage = /^image/.test(mimeType)
        const processedFile = isImage
          ? await convertToWebp(await resizeImageFile(originalFile, maxEdge), quality)
          : originalFile
        const uniqueId = uuidv4()
        const uri = URL.createObjectURL(processedFile)
        return {
          entry: {
            name: processedFile.name,
            type: getFileType(mimeType || processedFile.type || ''),
            size: processedFile.size,
            uri,
            uniqueId,
          },
          multipart: { file: processedFile, uniqueId },
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[ui2-fileUploadField] failed to process file', err)
        return null
      }
    }))

    const successful = processed.filter((p): p is NonNullable<typeof p> => p !== null)
    if (successful.length === 0) return

    this.registerMultipartFiles(successful.map((r) => r.multipart))
    this.props.onChange?.(
      withFilesetFiles(this.props.value as any, [...existing, ...successful.map((r) => r.entry)]),
    )
    const newIds = new Set(successful.map((r) => r.entry.uniqueId).filter(Boolean))
    this.setState({ recentlyAddedIds: newIds })
    setTimeout(() => this.setState({ recentlyAddedIds: new Set() }), 800)
  }

  protected handleRemoveAtIndex = (index: number) => {
    const next = this.getValue().slice()
    const [removed] = next.splice(index, 1)
    const { entity } = this.props
    if (entity && removed?.uniqueId) {
      entity.removeMultipartFileByIds(removed.uniqueId)
    }
    this.props.onChange?.(withFilesetFiles(this.props.value as any, next))
  }

  protected registerMultipartFiles(files: Array<{ file: File; uniqueId: string }>) {
    if (_.isEmpty(files)) return
    const { entity, frames } = this.props
    const valuePath = frames?.getContext?.('valuePath')
    if (!entity || !valuePath) return
    entity.addMultipartFiles(valuePath, files)
  }
}
